import React, { useContext, useMemo } from "react"
import { Button } from "dash-styled"
import { ObjectFieldTemplateProps, utils } from "@rjsf/core"
import { ReactComponent as TriangleIcon } from "icons/lib/triangle.svg"
import { StylingContext, ToolsContext } from "resha"
import { useBoolean } from "../../../../Hooks/useBoolean"

export function ParameterObjectTemplate({
    DescriptionField,
    description = "", // despite its type, description can be undefined
    TitleField,
    title = "", // despite its type, title can be undefined
    properties,
    required,
    schema,
    uiSchema,
    idSchema,
    formData,
    disabled,
    readonly,
    onAddClick
}: ObjectFieldTemplateProps) {
    const {
        shouldRenderObjectInPanel,
        shouldRenderFieldInPanel,
        invertLayout,
        transparentField
    } = useContext(StylingContext)
    const toolsContext = useContext(ToolsContext)
    const { skipObjectTitle = false, skipMultiSchemaTitle = false } =
        toolsContext
    const updatedToolsContext = useMemo(
        () => ({ ...toolsContext, skipMultiSchemaTitle: false }),
        [toolsContext]
    )
    const shouldOverrideRenderObjectInPanel =
        uiSchema["ui:options"]?.shouldRenderObjectInPanel
    const isBlock = uiSchema["ui:options"]?.isBlockType
    const [isExpanded, , , toggleExpanded] = useBoolean(
        shouldOverrideRenderObjectInPanel === false ||
            !shouldRenderObjectInPanel ||
            Object.keys(formData ?? {}).length > 0
    )

    const objectPropertiesStylingContext = useMemo(
        () => ({
            shouldRenderObjectInPanel: false,
            shouldRenderFieldInPanel: isBlock
                ? false
                : shouldRenderFieldInPanel,
            invertLayout,
            transparentField
        }),
        [shouldRenderFieldInPanel, isBlock, invertLayout, transparentField]
    )

    const shouldRenderTitleAndDescription = useMemo(
        () =>
            schema.properties !== undefined &&
            Object.values(schema.properties).filter(
                v =>
                    typeof v !== "object" ||
                    (v.const === undefined && v.type !== "null")
            ).length > 0,
        [schema.properties]
    )
    const addButton =
        !readonly &&
        !disabled &&
        utils.canExpand(schema, uiSchema, formData) ? (
            <Button
                className="mt-4"
                size="xs"
                color="plain"
                variant="contained2"
                onClick={onAddClick(schema)}
            >
                Add
            </Button>
        ) : undefined

    if (properties.length === 0 && addButton === undefined) {
        return null
    }

    if (
        properties.length === 1 &&
        addButton === undefined &&
        title === "" &&
        description === ""
    ) {
        // To mitigate nested object mess, render the only property of
        // an untitled object as if it's not embedded in an object
        return properties[0].content
    }

    if (
        !(isBlock && shouldRenderFieldInPanel) &&
        (!shouldRenderObjectInPanel ||
            shouldOverrideRenderObjectInPanel === false)
    ) {
        return (
            <ToolsContext.Provider value={updatedToolsContext}>
                <StylingContext.Provider value={objectPropertiesStylingContext}>
                    {shouldRenderTitleAndDescription &&
                    !skipObjectTitle &&
                    !skipMultiSchemaTitle ? (
                        <>
                            {(uiSchema["ui:title"] ?? title) && (
                                <TitleField
                                    id={`${idSchema.$id}-title`}
                                    title={title}
                                    required={required}
                                />
                            )}
                            {description && (
                                <DescriptionField
                                    id={`${idSchema.$id}-description`}
                                    description={description}
                                />
                            )}
                        </>
                    ) : null}
                    {properties.map((element, index, arr) => (
                        <div
                            key={element.name}
                            className={
                                element.hidden
                                    ? "hidden"
                                    : invertLayout
                                    ? ""
                                    : addButton !== undefined ||
                                      index < arr.length - 1
                                    ? "mb-2"
                                    : ""
                            }
                        >
                            {element.content}
                        </div>
                    ))}
                    {addButton}
                </StylingContext.Provider>
            </ToolsContext.Provider>
        )
    }

    return (
        <ToolsContext.Provider value={updatedToolsContext}>
            <StylingContext.Provider value={objectPropertiesStylingContext}>
                <div
                    className={
                        shouldRenderFieldInPanel && isBlock
                            ? "shadow-md rounded-xl p-4 bg-white"
                            : "rounded-lg p-4 border border-solid border-rhino-300"
                    }
                >
                    {title &&
                    shouldRenderTitleAndDescription &&
                    !skipObjectTitle &&
                    !skipMultiSchemaTitle ? (
                        <button
                            onClick={toggleExpanded}
                            className={`w-full px-0 text-left flex items-top -ml-4 ${
                                isExpanded ? "mb-1" : ""
                            }`}
                        >
                            <TriangleIcon
                                className={`w-1 flex-shrink-0 transition duration-200 ease-in-out mt-0.5 mr-1 ml-2 transform ${
                                    isExpanded ? "rotate-90" : ""
                                }`}
                            />
                            <div>
                                <TitleField
                                    id={`${idSchema.$id}-title`}
                                    title={title}
                                    required={required}
                                />

                                {description && (
                                    <DescriptionField
                                        id={`${idSchema.$id}-description`}
                                        description={description}
                                    />
                                )}
                            </div>
                        </button>
                    ) : null}
                    {isExpanded && (
                        <>
                            {properties.map((element, index, arr) => (
                                <div
                                    key={element.name}
                                    className={
                                        element.hidden
                                            ? "hidden"
                                            : invertLayout
                                            ? ""
                                            : addButton !== undefined ||
                                              index < arr.length - 1
                                            ? "mb-4"
                                            : ""
                                    }
                                >
                                    {element.content}
                                </div>
                            ))}
                            {addButton}
                        </>
                    )}
                </div>
            </StylingContext.Provider>
        </ToolsContext.Provider>
    )
}
